<template>
    <b-modal :id="modalId" size="xl" title="카테고리 선택" hide-footer @hidden="active = false">
        <div class="row">
            <!-- 카테고리 검색 -->
            <div class="col-12 mb-3">
                <div class="form-group">
                    <label for="categorySearch">카테고리 검색:</label>
                    <div class="input-group">
                        <input type="text" class="form-control" id="categorySearch" v-model="categorySearchTerm"
                            placeholder="카테고리 이름으로 검색 (Enter로 검색)" @keyup.enter="searchCategories">
                        <div class="input-group-append">
                            <button class="btn btn-primary" type="button" @click="searchCategories"
                                :disabled="searchingCategories || !categorySearchTerm.trim()">
                                <i class="ri-search-line mr-0" v-if="!searchingCategories"></i>
                                <b-spinner small v-else></b-spinner>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- 검색 결과 -->
                <div v-if="categorySearchResults.length > 0" class="mt-2 category-search-results">
                    <h6>검색 결과:</h6>
                    <div class="list-group">
                        <div v-for="category in categorySearchResults" :key="category.internalCategoryCode"
                            class="list-group-item list-group-item-action"
                            style="cursor: pointer; user-select: none;"
                            @click.stop.prevent="selectSearchedCategory(category)"
                            @mousedown.stop.prevent
                            @mouseup.stop.prevent
                            @touchstart.stop.prevent
                            @touchend.stop.prevent>
                            <span>{{ category.name }}</span>
                            <small class="d-block text-muted">{{ category.fullPathName }}</small>
                            <span v-if="category.leafCategory" class="float-right badge badge-success">최종</span>
                        </div>
                    </div>
                </div>

                <div v-if="noSearchResults && lastSearchTerm" class="alert alert-warning mt-2">
                    "{{ lastSearchTerm }}"에 대한 검색 결과가 없습니다.
                </div>
            </div>

            <div class="col-12 mb-3">
                <p>등록할 상품의 카테고리를 선택해주세요. 단계별로 선택하면 하위 카테고리가 표시됩니다.</p>
                <div class="d-flex flex-wrap">
                    <span v-for="(category, index) in selectedCategoryPath" :key="index"
                        class="d-flex align-items-center category-path-badge">
                        <span v-if="index > 0" class="mx-1">></span>
                        <b-badge variant="info" class="p-2">{{ category.displayItemCategoryDto.name }}</b-badge>
                    </span>
                </div>
            </div>

            <!-- 카테고리 선택 영역 -->
            <div class="col-12">
                <div class="row">
                    <!-- 동적으로 카테고리 컬럼 생성 -->
                    <div class="col-md-3 mb-3" v-for="(categories, depthIndex) in filteredCategorySelections"
                        :key="'depth-' + depthIndex">
                        <div class="card h-100">
                            <div class="card-header bg-light">
                                <h6 class="mb-0">{{ depthIndex + 1 }}단계 카테고리</h6>
                            </div>
                            <div class="card-body p-0" style="max-height: 400px; overflow-y: auto;">
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item cursor-pointer" v-for="category in categories"
                                        :key="category.displayItemCategoryDto.displayItemCategoryCode"
                                        :class="{ 'active': selectedCategoryPath[depthIndex] && selectedCategoryPath[depthIndex].displayItemCategoryDto.displayItemCategoryCode === category.displayItemCategoryDto.displayItemCategoryCode }"
                                        @click="selectCategory(category, depthIndex)">
                                        {{ category.displayItemCategoryDto.name }}
                                        <!-- 해당 카테고리가 최종 카테고리인지 표시 (leaf가 true인 경우) -->
                                        <span v-if="category.leaf" class="float-right badge badge-success">최종</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 버튼 영역 -->
            <div class="col-12 mt-4 text-right">
                <b-button variant="secondary" class="mr-2" @click="$bvModal.hide(modalId)">
                    취소
                </b-button>
                <b-button variant="primary" @click="completeSelection" :disabled="!canCompleteSelection">
                    선택 완료
                </b-button>
                <div v-if="!canCompleteSelection && selectedCategoryPath.length > 0" class="text-warning mt-2">
                    <small><i class="ri-information-line"></i> 최종 카테고리를 선택해야 합니다.</small>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
// 쿠팡 카테고리 고르기(검색·단계 선택) — AI 로켓등록 화면에서 그대로 옮겼다. 소싱관리 «상세 보기» 창의 AI 등록 초안도 쓴다.
// 확장(GET_CATEGORY_INFO · SEARCH_CATEGORIES)에 묻고, 고르면 selected({categoryCode, categoryPath, categoryPathString}) 를 낸다.
// 확장 응답에는 id 가 없어서 열려 있는(open 한) 쪽만 응답을 받는다 — 두 화면이 같이 떠 있어도 안 섞이게.
export default {
    name: 'CategorySelectModal',
    props: {
        // 한 화면에 둘이 뜰 수 있어 모달 id 를 받는다
        modalId: { type: String, default: 'categorySelectionModal' }
    },
    data() {
        return {
            active: false,
            selectedCategoryPath: [],
            categoryDepth: 0,
            categorySelections: [],

            // 카테고리 검색 관련 속성
            categorySearchTerm: '',
            categorySearchResults: [],
            searchingCategories: false,
            noSearchResults: false,
            lastSearchTerm: ''
        };
    },
    computed: {
        filteredCategorySelections() {
            return this.categorySelections.filter((categories) =>
                categories && categories.length > 0
            );
        },

        // 선택 완료 버튼 활성화 여부
        canCompleteSelection() {
            const lastSelectedCategory = this.selectedCategoryPath[this.selectedCategoryPath.length - 1];
            return lastSelectedCategory && lastSelectedCategory.leaf === true;
        }
    },
    mounted() {
        window.addEventListener('message', this.handleExtensionMessage);
    },
    beforeDestroy() {
        window.removeEventListener('message', this.handleExtensionMessage);
    },
    methods: {
        // 카테고리 선택 모달 표시 — 루트 카테고리 응답이 오면 모달이 뜬다(GET_CATEGORY_INFO_RESPONSE)
        open() {
            this.active = true;
            // 카테고리 초기화
            this.selectedCategoryPath = [];
            this.categoryDepth = 0;
            this.categorySelections = [];
            this.categorySearchTerm = '';
            this.categorySearchResults = [];
            this.noSearchResults = false;

            // 확장 프로그램에 메시지 전송 - 루트 카테고리 요청
            const message = {
                type: 'GET_CATEGORY_INFO',
                data: {
                    categoryCode: "",
                    suppliercode: this.$store.state.user.suppliercode
                }
            };

            console.log("루트 카테고리 요청:", message);
            window.postMessage(message, '*');
        },

        /**
         * 카테고리 검색 실행
         */
        searchCategories() {
            // 검색어가 너무 짧으면 실행하지 않음
            if (this.categorySearchTerm.trim().length < 2) {
                return;
            }

            // 검색 상태 설정
            this.searchingCategories = true;
            this.lastSearchTerm = this.categorySearchTerm.trim();
            this.noSearchResults = false;

            // 확장 프로그램에 메시지 전송
            const message = {
                type: 'SEARCH_CATEGORIES',
                data: {
                    keyword: this.categorySearchTerm.trim(),
                    suppliercode: this.$store.state.user.suppliercode
                }
            };

            console.log("카테고리 검색 요청:", message);
            window.postMessage(message, '*');
        },

        /**
         * 검색 결과에서 카테고리 선택
         * @param {Object} category - 선택된 카테고리 객체
         */
        selectSearchedCategory(category) {
            console.log('검색 결과에서 카테고리 선택:', category);

            // 현재 스크롤 위치 저장
            const modal = document.querySelector('#' + this.modalId);
            const modalBody = modal ? modal.querySelector('.modal-body') : null;
            const currentScrollTop = modalBody ? modalBody.scrollTop : 0;

            console.log('클릭 전 스크롤 위치:', currentScrollTop);


            // 스크롤 위치 복원 (DOM 업데이트 후)
            this.$nextTick(() => {
                if (modalBody && currentScrollTop > 0) {
                    modalBody.scrollTop = currentScrollTop;
                    console.log('스크롤 위치 복원:', currentScrollTop);
                }
            });

            // 카테고리 모달 닫기
            this.$bvModal.hide(this.modalId);

            // 선택한 카테고리 정보 — 받는 쪽이 상품 추가·대기열 행에 쓴다 (API 응답 구조 그대로)
            this.$emit('selected', {
                categoryCode: category.internalCategoryCode.toString(),
                categoryPath: category.fullPathName,
                categoryPathString: category.fullPathName
            });
        },

        // 카테고리 선택 핸들러
        selectCategory(category, depth) {
            console.log(`${depth}단계 카테고리 선택:`, category);

            // 선택된 카테고리 경로 업데이트 (Vue의 반응성을 위해 $set 사용)
            this.$set(this.selectedCategoryPath, depth, category);

            // 현재 depth 이후의 경로 초기화
            const newPath = this.selectedCategoryPath.slice(0, depth + 1);
            this.selectedCategoryPath = newPath;

            // 다음 단계 카테고리 요청
            this.categoryDepth = depth + 1;

            // leaf 속성이 true인 경우 더 이상 하위 카테고리가 없음
            if (category.leaf === true) {
                // 현재 카테고리가 최종 카테고리인 경우 하위 카테고리 요청하지 않음
                console.log('최종 카테고리 선택됨:', category.displayItemCategoryDto.name);

                // 더 이상의 하위 카테고리를 보여주지 않도록 초기화
                for (let i = this.categoryDepth; i < this.categorySelections.length; i++) {
                    this.$set(this.categorySelections, i, []);
                }
            } else {
                // 자식 카테고리 요청
                this.additionalCategoryCode(category.displayItemCategoryDto.displayItemCategoryCode);
            }
        },

        // 카테고리 선택 완료
        completeSelection() {
            // 마지막으로 선택된 카테고리 정보 가져오기
            const lastSelectedCategory = this.selectedCategoryPath[this.selectedCategoryPath.length - 1];

            if (!lastSelectedCategory) {
                this.$bvToast.toast('카테고리를 선택해주세요.', {
                    title: '알림',
                    variant: 'warning',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
                return;
            }

            // 선택된 카테고리가 최종 카테고리인지 확인
            if (lastSelectedCategory.leaf === false) {
                // 최종 카테고리가 아닌 경우 선택 완료를 막음
                this.$bvToast.toast('최종 카테고리를 선택해야 합니다. 하위 카테고리를 선택해주세요.', {
                    title: '알림',
                    variant: 'warning',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
                return;
            }

            // 카테고리 경로 문자열 생성 (UI 표시용)
            const categoryPathNames = this.selectedCategoryPath.map(cat =>
                cat.displayItemCategoryDto.name
            ).join(' > ');

            // 카테고리 선택 모달 닫기
            this.$bvModal.hide(this.modalId);

            // 선택된 카테고리 정보 — 받는 쪽이 상품 추가·대기열 행에 쓴다
            this.$emit('selected', {
                categoryCode: lastSelectedCategory.displayItemCategoryDto.displayItemCategoryCode,
                categoryPath: lastSelectedCategory.displayItemCategoryDto.categoryPath,
                categoryPathString: categoryPathNames
            });
        },

        additionalCategoryCode(code) {
            // 확장 프로그램에 메시지 전송
            const message = {
                type: 'GET_CATEGORY_INFO',
                data: {
                    categoryCode: code,
                    suppliercode: this.$store.state.user.suppliercode
                }
            };

            console.log(message)

            window.postMessage(message, '*');
        },

        handleExtensionMessage(event) {
            if (!this.active) return;
            if (!event.data || typeof event.data !== 'object') return;

        // 카테고리 검색 응답 처리
        if (event.data.type === 'SEARCH_CATEGORIES_RESPONSE') {
            this.searchingCategories = false;

            if (Array.isArray(event.data.results) && event.data.results.length > 0) {
                // 최종 카테고리만 필터링
                this.categorySearchResults = event.data.results.filter(category =>
                    category.leafCategory === true
                );
                this.noSearchResults = this.categorySearchResults.length === 0;

                if (this.categorySearchResults.length === 0 && event.data.results.length > 0) {
                    // 검색 결과는 있지만 최종 카테고리가 없는 경우
                    this.noSearchResults = true;
                    console.log('검색 결과에 최종 카테고리가 없습니다. 전체 결과:', event.data.results.length, '개');
                }
            } else {
                this.categorySearchResults = [];
                this.noSearchResults = true;
            }
            return;
        }

        // GET_CATEGORY_INFO_RESPONSE 처리 부분 수정
        if (event.data.type == 'GET_CATEGORY_INFO_RESPONSE') {
            // 받는 쪽 로딩 표시(대기열 추가 중 등)를 푼다
            this.$emit('responded');

            // 카테고리 정보 처리
            if (Array.isArray(event.data.categoryCode) && event.data.categoryCode.length > 0) {
                // 현재 depth에 맞는 카테고리 목록 저장
                // Vue의 반응성을 위해 $set 사용
                if (!this.categorySelections[this.categoryDepth]) {
                    this.$set(this.categorySelections, this.categoryDepth, event.data.categoryCode);
                } else {
                    this.$set(this.categorySelections, this.categoryDepth, event.data.categoryCode);
                }

                // 현재 depth 이후의 카테고리 정보는 초기화
                for (let i = this.categoryDepth + 1; i < this.categorySelections.length; i++) {
                    this.$set(this.categorySelections, i, []);
                }

                // 카테고리 선택 모달 표시
                this.$bvModal.show(this.modalId);
            } else {
                // 마지막 카테고리인 경우 (더 이상 하위 카테고리가 없는 경우)
                if (this.categoryDepth > 0 && this.selectedCategoryPath.length > 0) {
                    this.$bvToast.toast('선택한 카테고리의 하위 카테고리가 없습니다.', {
                        title: '알림',
                        variant: 'info',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });
                } else {
                    this.$bvToast.toast('카테고리 정보를 가져오는데 실패했습니다. \n Supplier를 새로고침 후 다시 시도해주세요.', {
                        title: '오류',
                        variant: 'danger',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });
                }
            }
            return;
        }
        }
    }
};
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.list-group-item.active {
    background-color: #007bff;
    border-color: #007bff;
    color: white;
}

.list-group-item:hover:not(.active) {
    background-color: #f8f9fa;
}

/* 카테고리 경로 스타일 */
.category-path-badge {
    display: inline-block;
    margin-right: 5px;
    margin-bottom: 5px;
}

/* 스크롤바 스타일 개선 */
.card-body::-webkit-scrollbar {
    width: 8px;
}

.card-body::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.card-body::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
}

.card-body::-webkit-scrollbar-thumb:hover {
    background: #888;
}
</style>
